import {LetrusApi} from '@letrustech/letrus-api-interfaces';
import Annotations from 'components/Annotations';
import AsideProposal from 'components/AsideProposal';
import WaitReviewSidebar from 'components/WaitReviewSidebar';
import ZeroGradeReasons from 'components/ZeroGradeReasons';
import {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {
  getCompositionJsonStatsAsync,
  getEditorState,
  getIsLoadingCompositionById,
  isLoadingCompositionsJsonStatsAsync,
  setHighlight,
} from 'store/reducers/compositions';
import {
  changeToggleCompletedTestReportStatus,
  getToggleStatus,
  SelectedToggle,
} from 'store/reducers/toggle';
import {useMobile} from 'utils/hooks';
import useTrackEvent from 'utils/hooks/useTrackEvent';
import {EventData} from 'utils/hooks/useTrackEvent/types';
import {fetchWritePreparation} from 'APIs/letrusAPI/writePreparation';
import {fetchZeroGradeComment} from 'APIs/letrusAPI/zeroGradeComment';
import {useParams} from 'react-router-dom';
import {Activity} from 'APIs/letrusAPI/activities/types';
import StatisticsSidebar from '../../../components/StatisticsSidebar';
import styles from './CompositionSidebar.module.scss';

export interface DispatchProps {
  setHighlight: typeof setHighlight;
  changeToggleCompletedTestReportStatus: typeof changeToggleCompletedTestReportStatus;
}

export interface StateProps {
  jsonStatsAsync: ImmutableMap<LetrusApi.JsonStatsAsyncResponse>;
  wordsNumber: number;
  toggleStatus: SelectedToggle;
  isLoadingComposition: boolean;
  isLoadingCompositionsJsonStatsAsync: boolean;
}

export interface OwnProps {
  composition: ImmutableMap<any>;
  learningPath?: Activity;
}

export type CompositionSidebarProps = OwnProps & DispatchProps & StateProps;

function CompositionSidebar({
  composition,
  setHighlight,
  jsonStatsAsync,
  wordsNumber,
  learningPath,
  toggleStatus,
  changeToggleCompletedTestReportStatus,
  isLoadingComposition,
  isLoadingCompositionsJsonStatsAsync,
}: CompositionSidebarProps): JSX.Element {
  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);

  const {isMobile} = useMobile();
  const {trackEvent} = useTrackEvent();
  const {composition_id} = useParams();

  const parentId = composition.get('parent_id');
  const hasWritePreparation = composition.get('uses_write_preparation');
  const writePreparation = fetchWritePreparation(composition_id, {
    isPaused: () => !composition.get('uses_write_preparation'),
  });

  const genreId = composition.getIn(['instruction_id', 'genre', 'id']);
  const genreName = composition.getIn(['instruction_id', 'genre', 'name']);
  const {data: zeroGradeCommentData} = fetchZeroGradeComment({
    genre_id: genreId ?? 0,
    user_role: 'student',
  });

  const isDynamicEditor =
    composition.getIn(['test', 'template', 'editor']) === 'dynamic';
  const isReviewGridMultigenre =
    composition.getIn(['instruction_id', 'genre', 'review_grid']) === 61;
  const isMultigenreRewriting = isReviewGridMultigenre && !!parentId;

  const eventData: EventData = {
    composition: {
      composition_id: composition.get('id'),
    },
    ...(learningPath
      ? {
          learningPathUserInstance: {
            learning_path_user_instance_id: learningPath.id,
            step_id:
              learningPath.steps?.find(
                (step) => step.order === learningPath.step_number,
              )?.id || 0,
          },
        }
      : {}),
    test: {
      test_id: composition.getIn(['test', 'id']),
    },
  };

  useEffect(() => {
    if (!isMobile) {
      if (isDynamicEditor) {
        changeToggleCompletedTestReportStatus('structure');
      } else {
        changeToggleCompletedTestReportStatus('proposal');
      }
    }
  }, [composition]);

  useEffect(() => {
    if (hasScrolledToEnd) {
      trackEvent('scrolled_to_end_of_proposal', eventData);
    }
  }, [hasScrolledToEnd]);

  return (
    <aside
      data-visible={`${!!toggleStatus}`}
      aria-hidden={!toggleStatus}
      data-testid="completed-test-report-aside"
      className={styles.wrapper}
    >
      <div className={styles.painel}>
        <button
          type="button"
          onClick={() => changeToggleCompletedTestReportStatus(null)}
        >
          X
        </button>
      </div>
      <section className={styles.content}>
        {(toggleStatus === 'structure' || toggleStatus === 'correction') &&
          (!isLoadingCompositionsJsonStatsAsync ? (
            <StatisticsSidebar
              composition={composition}
              wordsNumber={wordsNumber}
              setHighlight={setHighlight}
              jsonStatsAsync={jsonStatsAsync}
              hideCircularProgressBar={composition.get('flag_finished')}
            />
          ) : (
            <span>Carregando...</span>
          ))}

        {toggleStatus === 'proposal' && (
          <>
            {zeroGradeCommentData && zeroGradeCommentData.length > 0 && (
              <div className={styles.zeroGradeReasonsWrapper}>
                <ZeroGradeReasons
                  reasons={zeroGradeCommentData}
                  genreName={genreName}
                  subtitle="Atenção: conheça os motivos que podem zerar sua redação"
                />
              </div>
            )}
            <AsideProposal
              writePreparation={
                hasWritePreparation ? writePreparation.data : undefined
              }
              composition={composition}
              isLoading={
                isLoadingComposition ||
                (hasWritePreparation && writePreparation?.isLoading)
              }
              setHasScrolledToEnd={setHasScrolledToEnd}
            />
          </>
        )}

        {(toggleStatus === 'competencies' || toggleStatus === 'next-steps') && (
          <WaitReviewSidebar />
        )}

        {toggleStatus === 'annotations' &&
          (hasWritePreparation || isMultigenreRewriting) && (
            <Annotations writePreparation={writePreparation?.data} />
          )}
      </section>
    </aside>
  );
}

export default connect(
  (state: any) => {
    const eEditorState = getEditorState(state);
    const jsonStatsAsync = getCompositionJsonStatsAsync(state);
    const toggleStatus = getToggleStatus(state);
    const isLoadingComposition = getIsLoadingCompositionById(state);

    if (!eEditorState) {
      return {
        jsonStatsAsync,
      };
    }

    const {wordsNumber} = eEditorState;

    return {
      jsonStatsAsync,
      wordsNumber,
      toggleStatus,
      isLoadingComposition,
      isLoadingCompositionsJsonStatsAsync:
        isLoadingCompositionsJsonStatsAsync(state),
    };
  },
  {
    setHighlight,
    changeToggleCompletedTestReportStatus,
  },
)(CompositionSidebar);
